// src/components/OAuthLogViewer.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  Text,
  Flex,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

/**
 * OAuthLogViewer
 * Displays recent OAuth login activity for the current business.
 * Uses UUID identifiers (business_uuid).
 */
export default function OAuthLogViewer() {
  const { businessIdUuid, loading: authLoading } = useBusinessGuard();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    if (authLoading || !businessIdUuid) return;

    const fetchLogs = async () => {
      setLoading(true);

      try {
        const { data, error } = await supabase
          .from("oauth_log")
          .select(
            `
            id,
            triggered_at,
            provider,
            ip_address,
            device_type,
            source
          `
          )
          .eq("business_uuid", businessIdUuid)
          .order("triggered_at", { ascending: false });

        if (error) throw error;

        setLogs(data || []);
      } catch (err) {
        console.error("[OAuthLogViewer] Error:", err);
        toast({
          title: "Failed to load OAuth logs",
          description: err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [authLoading, businessIdUuid, toast]);

  if (authLoading || !businessIdUuid) return null;

  return (
    <Box
      bg="white"
      _dark={{ bg: "gray.800" }}
      p={4}
      rounded="md"
      shadow="md"
    >
      <Text fontWeight="bold" mb={3}>
        🔐 OAuth Login Activity
      </Text>

      {loading ? (
        <Flex justify="center" align="center" minH="100px">
          <Spinner />
        </Flex>
      ) : logs.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No login activity found.
        </Text>
      ) : (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Provider</Th>
              <Th>IP</Th>
              <Th>Device</Th>
              <Th>Source</Th>
            </Tr>
          </Thead>

          <Tbody>
            {logs.map((log) => (
              <Tr key={log.id}>
                <Td>
                  {log.triggered_at
                    ? new Date(log.triggered_at).toLocaleDateString()
                    : "—"}
                </Td>
                <Td>{log.provider || "—"}</Td>
                <Td>{log.ip_address || "—"}</Td>
                <Td>{log.device_type || "—"}</Td>
                <Td>{log.source || "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
